var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import difference from 'lodash/difference';
import intersection from 'lodash/intersection';
import union from 'lodash/union';
import { useApiResource } from './useApiResource';
import { useGlobalSelect } from './useGlobalSelect';
import { useCallback, useMemo, useRef } from 'react';
export function useSelectionTable(options) {
    var _a, _b;
    const { getRowKey, namespace, queryParams, resourceIdsUri, results = [], selectionType } = options;
    const { addSelection, getGlobalSelection, removeSelection } = useGlobalSelect();
    const previousResultsRef = useRef();
    const pauseApiCalls = Boolean(!results.length) || results === previousResultsRef.current;
    previousResultsRef.current = results;
    let filteredParams = queryParams;
    if (queryParams) {
        const { count, paginationDirection: direction, key, paginationDirection, key: pageKey } = queryParams, rest = __rest(queryParams, ["count", "paginationDirection", "key", "paginationDirection", "key"]);
        filteredParams = rest;
    }
    const { request, resource } = useApiResource(resourceIdsUri, filteredParams, pauseApiCalls);
    const allResourceIds = (_a = resource === null || resource === void 0 ? void 0 : resource.childSonarObjectIds) !== null && _a !== void 0 ? _a : resource === null || resource === void 0 ? void 0 : resource.sonarObjectIds;
    const globalSelection = useMemo(() => getGlobalSelection({ namespace }), [getGlobalSelection, namespace]);
    const resultsMap = useMemo(() => {
        const mapById = {};
        for (const record of results) {
            mapById[getRowKey(record)] = record;
        }
        return mapById;
    }, [getRowKey, results]);
    const selectedKeys = intersection(allResourceIds, globalSelection);
    const pageSelectedKeys = intersection(selectedKeys, results === null || results === void 0 ? void 0 : results.map(getRowKey));
    const updateSelection = useCallback((rowKeys, isSelected) => {
        var _a, _b;
        const selected = isSelected !== null && isSelected !== void 0 ? isSelected : true;
        const newSelectedRowKeys = selected ? union(selectedKeys, rowKeys) : difference(selectedKeys, rowKeys);
        if (newSelectedRowKeys.length !== selectedKeys.length) {
            if (selected) {
                const selections = {};
                for (const id of rowKeys) {
                    selections[Number(id)] = {
                        sonarObjectType: ((_b = (_a = resultsMap[id]) === null || _a === void 0 ? void 0 : _a.sonarObjectType) !== null && _b !== void 0 ? _b : selectionType)
                    };
                }
                addSelection({
                    namespace,
                    selections
                });
            }
            else {
                removeSelection({
                    namespace,
                    selections: rowKeys
                });
            }
        }
    }, [addSelection, namespace, removeSelection, resultsMap, selectedKeys, selectionType]);
    const resetSelection = useCallback(() => {
        updateSelection(selectedKeys !== null && selectedKeys !== void 0 ? selectedKeys : [], false);
    }, [selectedKeys, updateSelection]);
    const selectAll = useCallback(() => {
        updateSelection(allResourceIds !== null && allResourceIds !== void 0 ? allResourceIds : [], true);
    }, [allResourceIds, updateSelection]);
    const handleTableRowSelect = useCallback((record, isSelected) => {
        updateSelection([getRowKey(record)], isSelected);
    }, [getRowKey, updateSelection]);
    const handleTablePageSelectAll = useCallback((selected) => {
        const pageRecords = results !== null && results !== void 0 ? results : [];
        const selection = pageRecords.map(getRowKey);
        updateSelection(selection, selected);
    }, [getRowKey, results, updateSelection]);
    return {
        handleTablePageSelectAll,
        handleTableRowSelect,
        pageSelectedKeys,
        refreshIds: request,
        resetSelection,
        selectAll,
        selectedCount: selectedKeys.length,
        selectedKeys,
        totalCount: (_b = allResourceIds === null || allResourceIds === void 0 ? void 0 : allResourceIds.length) !== null && _b !== void 0 ? _b : 0,
        updateSelection
    };
}
